import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import moment from "moment";
import Spinner from "react-spinkit";
import {
  Paper,
  Grid,
  Typography,
  IconButton,
  Box,
  Fab,
  Dialog,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { DatePicker } from "@mui/x-date-pickers";
import numeral from "numeral";
import BarStackedTS from "./Graphs/BarStackedTS";
import DataExplorer from "./DataExplorer";
import Summary from "./stories/Summary";
import Stories from "./stories/Stories";
import Longform from "./longforms/Longform";
import LongformSummary from "./longforms/LongformSummary";
import SocialProductsSummary from "../social/Summary";
import ProjectForm from "../projects/ProjectForm";
import BigNumber from "./BigNumber";
import { setUserFavorites } from "../../redux/actions/index";
import kpiVocabulary from "../../vocabulary/kpis";
import { parseISO, formatISO, toDate } from "date-fns";
import { useGetReportByIdQuery } from "../../services/api";
import SocialProductsStories from "../social/Stories";
import SocialMediaStories from "./socialMedia/Stories";
import ReportExports from "./ReportExports";

const useStyles = makeStyles((theme) =>
  createStyles({
    paperGraph: {
      padding: theme.spacing(1),
      marginTop: "40px",
      backgroundColor: "#f7f7fa",
      border: "none",
    },
    storyPanelExpanded: {
      width: "100%",
      maxWidth: "840px",
      color: "#223647",
      [theme.breakpoints.down("sm")]: {},
    },
    storyPanelDFP: {
      borderTop: "2px solid #f7f7fa",
      boxShadow: "none",
      width: "100%",
      marginTop: "10px",
      marginBottom: "10px",
    },
    storiesContainer: {
      paddingLeft: "8px",
      paddingRight: "8px",
      [theme.breakpoints.down("sm")]: {
        paddingLeft: "0px",
        paddingRight: "0px",
      },
    },
    centeredTabs: {
      paddingLeft: "0px !important",
      paddingRight: "0px !important",
    },
    fabEdit: {
      position: "fixed",
      bottom: theme.spacing(3),
      right: theme.spacing(3),
      zIndex: 100,
      backgroundColor: "white",
      fontSize: "",
      color: "#223647",
      boxShadow:
        "0 2px 8px 0 rgba(21, 27, 32, 0.26), 0 1px 3px 0 rgba(21, 27, 32, 0.15)",
      [theme.breakpoints.up("lg")]: {
        top: "125px",
        right: "calc(50% - 500px)",
      },
      "&:hover": {
        backgroundColor: "#f7f7fa",
      },
    },
    fabGoBack: {
      position: "fixed",
      bottom: theme.spacing(3),
      left: theme.spacing(3),
      zIndex: 100,
      backgroundColor: "white",
      fontSize: "",
      color: "#223647",
      boxShadow:
        "0 2px 8px 0 rgba(21, 27, 32, 0.26), 0 1px 3px 0 rgba(21, 27, 32, 0.15)",
      [theme.breakpoints.up("lg")]: {
        top: "125px",
        left: "calc(50% - 500px)",
      },
      "&:hover": {
        backgroundColor: "#f7f7fa",
      },
    },
    dateInput: {
      boxShadow: "none",
    },
    dateControl: {
      boxShadow: "none",
      transform: "translate(0, 4px)",
      "& .MuiInputBase-input": {
        boxShadow: "none",
      },
      "& > div > input": {
        paddingTop: "0px",
        paddingBottom: "0px",
        maxWidth: "90px",

        fontSize: "18px",
        height: "24px",
        color: "#5e686c",
      },
    },
    modalPaper: {
      backgroundColor: "#f7f7fa",
      border: "2px solid #000",
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2, 4, 3),
      outline: "none",
    },
    gridNoMargin: {
      margin: 0,
    },
    outboundClickUrlContainer: {
      "&&&": {
        paddingLeft: "0px",
        paddingRight: "0px",
        paddingBottom: "0px",
        paddingTop: "0px",
        overflowWrap: "break-word",
        fontWeight: "400",
      },
    },
    outboundClickValueContainer: {
      "&&&": {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        fontWeight: "700",
        paddingBottom: "0px",
        paddingTop: "0px",
      },
    },
    clickTableRow: {
      borderBottom: "1px solid #e4e4e4",
      "&&&": {
        paddingLeft: "0px",
        paddingRight: "0px",
        paddingTop: "0px",
        paddingBottom: "0px",
      },
    },
    clickTableRowInner: {
      paddingTop: "8px",
      paddingBottom: "8px",
      paddingLeft: "0px",
      paddingRight: "0px",
      margin: "0px",
      width: "100%",
    },
    overviewRow: {
      paddingTop: "0px!important",
      paddingBottom: "0px!important",
      [theme.breakpoints.down("sm")]: {
        "&&&": {
          paddingLeft: "16px",
          paddingRight: "16px",
        },
      },
    },
    overviewTypography: {
      lineHeight: "1.6",
    },
    overviewKey: {
      display: "inline-block",
      fontSize: "18px",
      fontWeight: "200",
      color: "#5e686c",
      lineHeight: "0.94",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
    },
    overviewValue: {
      display: "inline-block",
      fontStyle: "bold",
      fontSize: "20px",
      paddingLeft: "5px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "18px",
      },
    },
    overviewKpiWrapperDesktop: {
      marginTop: "30px",
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
    overviewKpiWrapperMobile: {
      marginTop: "30px",

      display: "none",
      [theme.breakpoints.down("sm")]: {
        display: "flex",
        "&&&": {
          paddingLeft: "16px",
          paddingRight: "16px",
        },
      },
    },
    loadingWrapper: {
      width: "80px",
      marginTop: "40vh",
      marginLeft: "calc(50% - 40px)",
      [theme.breakpoints.down("sm")]: {
        marginTop: "50%",
      },
    },
    dateField: {
      "& input": {
        boxShadow: "none",
        width: "100%",
      },
    },
    graphContainer: {
      paddingLeft: "10%",
      paddingRight: "10%",
    },
    graphTitleContainer: {
      [theme.breakpoints.down("sm")]: {
        alignSelf: "center",
      },
    },
    graphTitle: {
      color: "#223647",
      fontSize: "22px",
      fontWeight: "700",
      [theme.breakpoints.down("sm")]: {
        fontSize: "18px",
      },
    },
    storyInfosWrapper: {
      display: "flex",
      flexDirection: "row",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
    },
    storyInfos: {
      color: "#262627",
    },
    storyInfosKey: {
      fontWeight: "bold",
      marginRight: "5px",
      fontSize: "16px",
    },
    storyInfosValue: {
      marginRight: "10px",
      fontWeight: "200",
    },
    dfpCreativeWrapper: {
      marginBottom: "37px",
      [theme.breakpoints.down("sm")]: {
        marginBottom: "14px",
      },
    },
    dfpCreativeTitle: {
      fontSize: "18px",
      color: "#262627",
      lineHeight: "1.11",
      maxWidth: "443px",
      marginBottom: "8px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
    },
    dfpMetricsKey: {
      color: "#5e686c",
      fontSize: "16px",
      display: "inline-block",
      fontFamily: "BatonTurbo-Book",
      lineHeight: "1.06",
      fontWeight: "normal",
      marginRight: "6px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
    },
    dfpMetricsValue: {
      color: "#5e686c",
      fontSize: "16px",
      display: "inline-block",
      marginRight: "25px",
      fontFamily: "BatonTurbo-Book",
      lineHeight: "1.06",
      fontWeight: "normal",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
    },
    dfpMetricsKeyBold: {
      color: "#5e686c",
      fontSize: "16px",
      display: "inline-block",
      fontFamily: "BatonTurbo-Book",
      lineHeight: "1.06",
      fontWeight: "700",
      marginRight: "6px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
    },
    dfpMetricsValueBold: {
      color: "#5e686c",
      fontSize: "16px",
      display: "inline-block",
      marginRight: "25px",
      fontFamily: "BatonTurbo-Book",
      lineHeight: "1.06",
      fontWeight: "700",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
    },
    dfpMetricsWrapper: {
      marginTop: "20px",
      [theme.breakpoints.down("sm")]: {
        marginTop: "4px",
      },
    },
    accordionSummaryInner: {
      padding: "0px 8px",
    },
    accordionSummaryDetails: {
      padding: "0px 8px 30px 8px",
    },
    calendarIconAdornment: {
      "& > button": {
        padding: "0px",
      },
      "& > input": {
        maxWidth: "50px",
      },
    },
    ogAccordionSummaryText: {
      color: "#376a93",
      [theme.breakpoints.down("sm")]: {
        fontSize: "16px",
      },
    },
    teaserAccordionSummaryText: {
      color: "#376a93",
      [theme.breakpoints.down("sm")]: {
        fontSize: "16px",
      },
    },
    ogDateRange: {
      color: "#5e686c",
      fontSize: "16px",
      fontWeight: "200",
      lineHeight: "1.06",
      marginBottom: "7px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
    },
    ogTitle: {
      fontSize: "18px",
      color: "#262627",
      lineHeight: "1.11",
      maxWidth: "443px",
      marginBottom: "6px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
    },
    ogDescription: {
      fontSize: "14px",
      fontWeight: "200",
      maxWidth: "443px",
      color: "#262627",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
    },
    datePickerIcon: {
      width: "23.4px",
      height: "23.4px",
      [theme.breakpoints.down("sm")]: {
        width: "14px",
        height: "14px",
      },
    },
    graphToggleButtonContainer: {
      display: "none",
      [theme.breakpoints.down("sm")]: {
        display: "flex",
        justifyContent: "flex-end",
      },
    },
    mediaTitlesSelected: {
      fontWeight: "200",
      color: "#223647",
      display: "inline-block",
      marginRight: "15px",
    },
    mediaTitlesNotSelected: {
      fontWeight: "200",
      color: "#aeb5bd",
      display: "inline-block",
      marginRight: "15px",
    },
    mediaTitleHeader: {
      fontSize: "16px",
      color: "#223647",
      marginRight: "15px",
    },
    imgBoxSquare: {
      width: "100%",
      position: "relative",
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      paddingBottom: "100%",
    },
    imgBoxSquareInner: {
      position: "absolute",
      top: "0",
      bottom: "0",
      width: "100%",
      height: "100%",
    },
    contentHubOverviewTitle: {
      fontSize: "18px",
      color: "#262627",
      lineHeight: "1.11",
      marginBottom: "6px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
    },
    contentHubOverviewSubtitle: {
      fontSize: "16px",
      color: "#262627",
      lineHeight: "1.11",
      marginBottom: "6px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
      },
    },
  }),
);

const Loading = ({ classes }) => (
  <Grid
    container
    spacing={2}
    style={{
      backgroundColor: "#f7f7fa",
      padding: "0px",
      margin: "0px",
    }}
  >
    <Grid item xs={12} sm={12} md={12}>
      <Spinner
        className={classes.loadingWrapper}
        name="pacman"
        color="#223647"
      />
    </Grid>
  </Grid>
);

const ErrorMessage = ({ message, isAuthError }) => {
  const navigate = useNavigate();

  const handleLogInClick = () => {
    navigate("/login");
  };

  const handleLoadAsGuestClick = () => {
    window.location.reload();
  };

  return (
    <Grid
      container
      spacing={2}
      style={{
        backgroundColor: "#f7f7fa",
        padding: "0px",
        margin: "0px",
      }}
    >
      <Grid item xs={12} sm={12} md={12}>
        <Box
          sx={{
            width: "380px",
            marginTop: {
              xs: "50%",
              md: "40vh",
            },
            padding: "28px",
            textAlign: "center",
            marginLeft: "calc(50% - 140px)",
            backgroundColor: "white",
            boxShadow:
              "0 2px 8px 0 rgba(21, 27, 32, 0.26), 0 1px 3px 0 rgba(21, 27, 32, 0.15)",
          }}
        >
          {isAuthError ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                You got logged out
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                <Button onClick={handleLogInClick}>Log in</Button>
                <Button onClick={handleLoadAsGuestClick}>Load as guest</Button>
              </Grid>
            </Grid>
          ) : (
            message || "Unknown error"
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

const Report = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userFavorites = useSelector((state) => state.favorites);
  const isLoggedIn = useSelector((state) => state.isLoggedIn);
  const isAdmin = useSelector((state) => state.isAdmin);
  const [projectData, setProjectData] = useState({});
  const [reportData, setReportData] = useState(null);
  const [reportOverviewData, setReportOverviewData] = useState({});
  const [storySelection, setStorySelection] = useState([]);
  const [storyKPIs, setStoryKPIs] = useState(null);
  const [campaignKPIs, setCampaignKPIs] = useState(null);
  const [contentHubOverview, setContentHubOverview] = useState(null);
  const [contentHubDetails, setContentHubDetails] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [timeSeriesBarData, setTimeSeriesBarData] = useState({});
  const [isFavorite, setIsFavorite] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [showPageviewGraph, setShowPageviewGraph] = useState(true);

  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParams = useMemo(
    () => ({
      id,
      dateFrom: searchParams.get("dateFrom"),
      dateTo: searchParams.get("dateTo"),
    }),
    [id, searchParams],
  );

  const {
    data: report,
    isFetching,
    isError,
    error,
  } = useGetReportByIdQuery(queryParams);

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const getReport = async (data) => {
    if (data === undefined) return;

    setStorySelection(data.storiesByLanguage);
    setProjectData(data.project);
    setReportOverviewData(data.reportOverview);
    setTimeSeriesBarData(data.timeSeriesBar);
    setCampaignKPIs(data.campaignKPIs);
    setStoryKPIs(data.storyKPIs);
    setContentHubDetails(data.contentHub.details);
    setContentHubOverview(data.contentHub.overview);
    setReportData(data.raw);
    setDateTo(data.dateRange.to);
    setDateFrom(data.dateRange.from);
  };

  const setDateRange = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("dateFrom", moment(dateFrom).format("YYYY-MM-DD"));
      newParams.set("dateTo", moment(dateTo).format("YYYY-MM-DD"));
      return newParams;
    });
  };

  const handleDateChange = (fieldName) => (date) => {
    if (date !== "Invalid Date") {
      if (fieldName === "dateFrom") {
        setDateFrom(formatISO(date));
      } else if (fieldName === "dateTo") {
        setDateTo(formatISO(date));
      }
    }
  };

  const favProject = async (id) => {
    try {
      const resp = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/v1/projects/fav/${id}`,
        {},
        {
          withCredentials: true,
          headers: {
            Accept: "application/json",
          },
        },
      );

      dispatch(setUserFavorites(resp.data.favorites));
    } catch (err) {
      console.error("Error adding project to favorites", err);
    }
  };

  useEffect(() => {
    getReport(report);
  }, [report]);

  useEffect(() => {
    if (
      Array.isArray(userFavorites) &&
      userFavorites.includes(projectData._id)
    ) {
      setIsFavorite(true);
    } else {
      setIsFavorite(false);
    }
  }, [userFavorites, projectData]);

  const toggleShowPageviewGraph = () => {
    setShowPageviewGraph(!showPageviewGraph);
  };

  const stories = storySelection;
  const abTests = reportData && reportData.abTests;
  const gaSummarized = reportData && reportData.gaSummarized;

  if (isError) {
    const errorMessage =
      error?.formattedMessage ||
      error?.message ||
      error?.originalError?.formattedMessage ||
      "An unknown error occurred";
    return (
      <ErrorMessage message={errorMessage} isAuthError={error.isAuthError} />
    );
  }

  if (isFetching || reportData === null) {
    return (
      <Grid item xs={12} sm={12} md={12}>
        <Box
          sx={{
            marginTop: {
              xs: 0,
              md: "40px",
            },
          }}
        >
          <Loading classes={classes} />
        </Box>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      {isLoggedIn && (
        <>
          <Fab
            className={classes.fabGoBack}
            color="secondary"
            aria-label="back"
            onClick={() => navigate(-1)}
          >
            <Box style={{ display: "flex" }}>
              <img
                src="/img/icons/chevron-left.svg"
                alt="Back"
                style={{ width: "28px" }}
              />
            </Box>
          </Fab>
          <Fab
            className={classes.fabEdit}
            color="secondary"
            aria-label="edit"
            onClick={handleOpenDialog}
            elevation={0}
          >
            <Box style={{ display: "flex" }}>
              <img
                src="/img/icons/edit-2.svg"
                alt="Edit"
                style={{ width: "28px" }}
              />
            </Box>
          </Fab>
        </>
      )}

      <>
        <Dialog
          open={dialogOpen}
          closeAfterTransition
          onClose={handleCloseDialog}
          maxWidth="md"
        >
          <div className={classes.modalPaper}>
            <ProjectForm
              project={reportData.project}
              handleCloseDialog={handleCloseDialog}
              refreshReport={getReport}
            />
          </div>
        </Dialog>

        <Grid item xs={12} sm={12} md={12}>
          <Paper
            elevation={0}
            variant="outlined"
            square
            sx={{
              marginTop: {
                xs: 0,
                md: "40px",
              },
              marginLeft: "-8px",
              marginRight: "8px",
              padding: "16px",
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                paddingTop: {
                  xs: "30px",
                  lg: "24px",
                },
                paddingBottom: {
                  xs: "10px",
                  lg: "24px",
                },
                fontFamily: "BatonTurbo-Book",
              }}
            >
              <Grid
                item
                xs={10}
                sm={10}
                md={10}
                className={classes.overviewRow}
              >
                <Typography variant="h6" className={classes.overviewTypography}>
                  {reportData && reportData.CRASS ? (
                    <>
                      <div className={classes.overviewKey}>Order number:</div>
                      <div className={classes.overviewValue}>
                        {reportData.CRASS.order_number}
                      </div>
                    </>
                  ) : (
                    ""
                  )}
                </Typography>
              </Grid>

              {isLoggedIn && (
                <Grid
                  item
                  xs={2}
                  sm={2}
                  md={2}
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "0px",
                    paddingTop: "0px!important",
                    paddingBottom: "0px!important",
                  }}
                >
                  <Box
                    color={isFavorite ? "secondary" : "default"}
                    aria-label="favorite"
                    onClick={() => favProject(projectData._id)}
                  >
                    {isFavorite ? (
                      <img
                        src="/img/icons/Star--filled.svg"
                        alt="Favorite"
                        style={{ width: "28px", height: "28px" }}
                      />
                    ) : (
                      <img
                        src="/img/icons/Star.svg"
                        alt="Add to favorites"
                        style={{ width: "28px", height: "28px" }}
                      />
                    )}
                  </Box>
                </Grid>
              )}

              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                className={classes.overviewRow}
              >
                <Typography variant="h6" className={classes.overviewTypography}>
                  {reportOverviewData.client ? (
                    <>
                      <div className={classes.overviewKey}>Customer:</div>
                      <div className={classes.overviewValue}>
                        {reportOverviewData.client}
                      </div>
                    </>
                  ) : (
                    ""
                  )}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                className={classes.overviewRow}
              >
                <Typography variant="h6" className={classes.overviewTypography}>
                  {reportOverviewData.name ? (
                    <>
                      <div className={classes.overviewKey}>Campaign:</div>
                      <div className={classes.overviewValue}>
                        {reportOverviewData.name}
                      </div>
                    </>
                  ) : (
                    ""
                  )}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                className={classes.overviewRow}
              >
                <Typography variant="h6" className={classes.overviewTypography}>
                  {projectData ? (
                    <>
                      <div className={classes.overviewKey}>Time period:</div>
                      <div className={classes.overviewValue}>
                        {`${moment(projectData.daterangeFrom).format("DD.MM.YY")} - ${moment(
                          projectData.daterangeTo,
                        ).format("DD.MM.YY")}`}
                      </div>
                    </>
                  ) : (
                    ""
                  )}
                </Typography>
              </Grid>
              {campaignKPIs && (
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  className={classes.overviewKpiWrapperDesktop}
                >
                  <Grid container spacing={2}>
                    {/* In case it's contentHub, we need titles */}
                    {reportData &&
                      reportData.project &&
                      reportData.project.isContentHub && (
                        <>
                          <Grid
                            item
                            md={12}
                            className={classes.contentHubOverviewTitle}
                          >
                            Content Hub Data
                          </Grid>

                          {Array.isArray(contentHubOverview) &&
                            contentHubOverview.length > 0 &&
                            contentHubOverview.map((co, i) => (
                              <Grid item md={12} key={i}>
                                <Grid container spacing={2}>
                                  <Grid
                                    item
                                    md={12}
                                    className={
                                      classes.contentHubOverviewSubtitle
                                    }
                                  >
                                    {co.key}
                                  </Grid>
                                  <BigNumber
                                    title={
                                      kpiVocabulary.screenPageViews.description
                                    }
                                    description={
                                      kpiVocabulary.screenPageViews.definition
                                    }
                                    value={
                                      co.value ? co.value.screenPageViews : null
                                    }
                                    color="#3C5F7C"
                                  />
                                  <BigNumber
                                    title={
                                      kpiVocabulary.averageSessionDuration
                                        .description
                                    }
                                    description={
                                      kpiVocabulary.averageSessionDuration
                                        .definition
                                    }
                                    value={
                                      co.value
                                        ? co.value.averageSessionDuration
                                        : null
                                    }
                                    unit="s"
                                    color="#3C5F7C"
                                  />
                                </Grid>
                              </Grid>
                            ))}

                          {Array.isArray(contentHubDetails) &&
                            contentHubDetails.length > 0 && (
                              <Grid
                                item
                                md={12}
                                style={{
                                  marginTop: "16px",
                                  marginBottom: "16px",
                                }}
                              >
                                <Accordion square>
                                  <AccordionSummary
                                    expandIcon={
                                      <img
                                        src="/img/icons/chevron-down.svg"
                                        alt="Expand"
                                        style={{
                                          width: "28px",
                                          height: "28px",
                                        }}
                                      />
                                    }
                                    className={classes.accordionSummaryInner}
                                  >
                                    <div
                                      className={
                                        classes.contentHubOverviewSubtitle
                                      }
                                    >
                                      Detailed Content Hub Data
                                    </div>
                                  </AccordionSummary>
                                  <AccordionDetails
                                    style={{
                                      paddingLeft: "8px",
                                      paddingRight: "24px",
                                    }}
                                  >
                                    <Grid
                                      container
                                      className={classes.gridNoMargin}
                                      spacing={2}
                                    >
                                      <Grid
                                        item
                                        md={12}
                                        className={classes.clickTableRow}
                                      >
                                        <Grid
                                          container
                                          className={classes.clickTableRowInner}
                                          spacing={2}
                                        >
                                          <Grid
                                            item
                                            xs={6}
                                            sm={6}
                                            md={6}
                                            className={
                                              classes.outboundClickUrlContainer
                                            }
                                          >
                                            URL
                                          </Grid>
                                          <Grid
                                            item
                                            xs={3}
                                            sm={3}
                                            md={3}
                                            className={
                                              classes.outboundClickValueContainer
                                            }
                                          >
                                            Pageviews
                                          </Grid>
                                          <Grid
                                            item
                                            xs={3}
                                            sm={3}
                                            md={3}
                                            className={
                                              classes.outboundClickValueContainer
                                            }
                                          >
                                            Avg Time on Page
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                      {contentHubDetails
                                        .toSorted(
                                          (a, b) =>
                                            b.value.screenPageViews -
                                            a.value.screenPageViews,
                                        )
                                        .map((hd, idhd) => (
                                          <Grid
                                            item
                                            md={12}
                                            className={classes.clickTableRow}
                                            key={idhd}
                                          >
                                            <Grid
                                              container
                                              className={
                                                classes.clickTableRowInner
                                              }
                                              spacing={2}
                                            >
                                              <Grid
                                                item
                                                xs={6}
                                                sm={6}
                                                md={6}
                                                className={
                                                  classes.outboundClickUrlContainer
                                                }
                                              >
                                                {hd.key}
                                              </Grid>
                                              <Grid
                                                item
                                                xs={3}
                                                sm={3}
                                                md={3}
                                                className={
                                                  classes.outboundClickValueContainer
                                                }
                                              >
                                                {hd.value &&
                                                hd.value.screenPageViews
                                                  ? hd.value.screenPageViews
                                                  : "n/a"}
                                              </Grid>
                                              <Grid
                                                item
                                                xs={3}
                                                sm={3}
                                                md={3}
                                                className={
                                                  classes.outboundClickValueContainer
                                                }
                                              >
                                                {hd.value &&
                                                hd.value.averageSessionDuration
                                                  ? numeral(
                                                      hd.value
                                                        .averageSessionDuration,
                                                    ).format("0.0")
                                                  : "n/a"}
                                              </Grid>
                                            </Grid>
                                          </Grid>
                                        ))}
                                    </Grid>
                                  </AccordionDetails>
                                </Accordion>
                              </Grid>
                            )}
                        </>
                      )}

                    {stories && stories.length > 0 && (
                      <Summary
                        kpis={campaignKPIs}
                        gaSummarized={gaSummarized}
                      />
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>

            {/*TODO: refactor to use socialProducts */}
            <SocialProductsSummary
              reports={reportData.socialMediaProducts}
              stories={reportData.socialMediaStories}
            />

            <LongformSummary
              longforms={reportData.longforms.longForms}
              aggregationTotal={reportData.longforms.aggregationTotal}
            />

            <Grid
              container
              spacing={2}
              sx={{
                paddingTop: {
                  xs: "30px",
                  lg: "24px",
                },
                paddingBottom: {
                  xs: "10px",
                  lg: "24px",
                },
                fontFamily: "BatonTurbo-Book",
              }}
            >
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                className={classes.overviewRow}
              >
                <Typography
                  variant="h6"
                  className={classes.overviewTypography}
                  style={{ paddingBottom: "16px" }}
                >
                  {projectData ? (
                    <>
                      <div className={classes.overviewKey}>
                        Change time period:
                      </div>
                      <div className={classes.overviewValue}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                          }}
                        >
                          <DatePicker
                            className={classes.dateControl}
                            id="date-picker-dialog-from"
                            format="dd.MM.yy"
                            //TODO: move into component?
                            //TODO: handle update
                            value={parseISO(dateFrom) || toDate(new Date())}
                            onChange={handleDateChange("dateFrom")}
                            slotProps={{
                              textField: {
                                id: "date-picker-dialog-from",
                                sx: {
                                  "& .MuiInputBase-root": {
                                    boxShadow: "none",
                                    maxWidth: "240px",
                                  },
                                  "& .MuiInputBase-input": {
                                    fontWeight: 700,
                                    fontSize: "18px",
                                    paddingTop: 0,
                                    height: "24px",
                                    fontStyle: "normal",
                                  },
                                },
                              },
                              inputAdornment: {
                                position: "start",
                              },
                            }}
                            slots={{
                              openPickerIcon: () => (
                                <img
                                  src="/img/icons/calendar-grey.svg"
                                  alt="Pick from date"
                                  style={{ width: "28px", height: "28px" }}
                                />
                              ),
                            }}
                          />
                          <DatePicker
                            style={{ boxShadow: "none" }}
                            className={classes.dateControl}
                            id="date-picker-dialog-to"
                            format="dd.MM.yy"
                            value={parseISO(dateTo) || toDate(new Date())}
                            onChange={handleDateChange("dateTo")}
                            slotProps={{
                              textField: {
                                id: "date-picker-dialog-from",
                                sx: {
                                  "& .MuiInputBase-root": {
                                    boxShadow: "none",
                                    maxWidth: "240px",
                                  },
                                  "& .MuiInputBase-input": {
                                    fontWeight: 700,
                                    fontSize: "18px",
                                    paddingTop: 0,
                                    height: "24px",
                                    fontStyle: "normal",
                                  },
                                },
                              },
                              inputAdornment: {
                                position: "start",
                              },
                            }}
                            slots={{
                              openPickerIcon: () => (
                                <img
                                  src="/img/icons/calendar-grey.svg"
                                  alt="Pick from date"
                                  style={{ width: "28px", height: "28px" }}
                                />
                              ),
                            }}
                          />
                          <Box
                            style={{
                              display: "inline-block",
                              alignItems: "center",
                              transform: "translate(0, 4px)",
                            }}
                          >
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setDateRange();
                              }}
                              style={{
                                transform: "translate(0px, 6px)",
                                textTransform: "none",
                                borderRadius: "3px",
                                boxShadow: "0 1px 1px 0 rgba(0, 0, 0, 0.09)",
                                border: "solid 2px #223647",
                                color: "#223647",
                                width: "112px",
                              }}
                            >
                              Update
                            </Button>
                          </Box>
                        </div>
                      </div>
                    </>
                  ) : (
                    ""
                  )}
                </Typography>
                {id && (
                  <ReportExports
                    projectId={id}
                    dateFrom={queryParams.dateFrom ?? undefined}
                    dateTo={queryParams.dateTo ?? undefined}
                    projectType={report?.project?.projectType}
                  />
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {campaignKPIs && (
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            className={classes.overviewKpiWrapperMobile}
          >
            <Grid container spacing={2}>
              {reportData &&
                reportData.project &&
                reportData.project.isContentHub && (
                  <>
                    <Grid
                      item
                      md={12}
                      sm={12}
                      xs={12}
                      className={classes.contentHubOverviewTitle}
                    >
                      Content Hub Data
                    </Grid>
                    {Array.isArray(contentHubOverview) &&
                      contentHubOverview.length > 0 &&
                      contentHubOverview.map((co, i) => (
                        <Grid item md={12} key={i}>
                          <Grid container spacing={2}>
                            <Grid
                              item
                              md={12}
                              sm={12}
                              xs={12}
                              className={classes.contentHubOverviewSubtitle}
                            >
                              {co.key}
                            </Grid>
                            <BigNumber
                              title={kpiVocabulary.screenPageViews.description}
                              description={
                                kpiVocabulary.screenPageViews.definition
                              }
                              value={co.value ? co.value.screenPageViews : null}
                              color="#3C5F7C"
                            />
                            <BigNumber
                              title={
                                kpiVocabulary.averageSessionDuration.description
                              }
                              description={
                                kpiVocabulary.averageSessionDuration.definition
                              }
                              value={
                                co.value
                                  ? co.value.averageSessionDuration
                                  : null
                              }
                              unit="s"
                              color="#3C5F7C"
                            />
                          </Grid>
                        </Grid>
                      ))}

                    {Array.isArray(contentHubDetails) &&
                      contentHubDetails.length > 0 && (
                        <Grid
                          item
                          md={12}
                          style={{
                            marginTop: "16px",
                            marginBottom: "16px",
                          }}
                        >
                          <Accordion square>
                            <AccordionSummary
                              expandIcon={
                                <img
                                  src="/img/icons/chevron-down.svg"
                                  alt="Expand"
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                  }}
                                />
                              }
                              className={classes.accordionSummaryInner}
                            >
                              <div
                                className={classes.contentHubOverviewSubtitle}
                              >
                                Detailed Content Hub Data
                              </div>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid
                                container
                                className={classes.gridNoMargin}
                                spacing={2}
                              >
                                <Grid
                                  item
                                  md={12}
                                  sm={12}
                                  xs={12}
                                  className={classes.clickTableRow}
                                >
                                  <Grid
                                    container
                                    className={classes.clickTableRowInner}
                                    spacing={2}
                                  >
                                    <Grid
                                      item
                                      xs={6}
                                      sm={6}
                                      md={6}
                                      className={
                                        classes.outboundClickUrlContainer
                                      }
                                      style={{
                                        alignItems: "flex-end",
                                        alignSelf: "flex-end",
                                      }}
                                    >
                                      URL
                                    </Grid>
                                    <Grid
                                      item
                                      xs={3}
                                      sm={3}
                                      md={3}
                                      className={
                                        classes.outboundClickValueContainer
                                      }
                                      style={{ alignItems: "flex-end" }}
                                    >
                                      Pageviews
                                    </Grid>
                                    <Grid
                                      item
                                      xs={3}
                                      sm={3}
                                      md={3}
                                      className={
                                        classes.outboundClickValueContainer
                                      }
                                      style={{ alignItems: "flex-end" }}
                                    >
                                      Avg Time on Page
                                    </Grid>
                                  </Grid>
                                </Grid>
                                {contentHubDetails.map((hd, idhd) => (
                                  <Grid
                                    item
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    className={classes.clickTableRow}
                                    key={idhd}
                                  >
                                    <Grid
                                      container
                                      className={classes.clickTableRowInner}
                                      spacing={2}
                                    >
                                      <Grid
                                        item
                                        xs={6}
                                        sm={6}
                                        md={6}
                                        className={
                                          classes.outboundClickUrlContainer
                                        }
                                      >
                                        {hd.key}
                                      </Grid>
                                      <Grid
                                        item
                                        xs={3}
                                        sm={3}
                                        md={3}
                                        className={
                                          classes.outboundClickValueContainer
                                        }
                                      >
                                        {hd.value && hd.value.screenPageViews
                                          ? hd.value.screenPageViews
                                          : "n/a"}
                                      </Grid>
                                      <Grid
                                        item
                                        xs={3}
                                        sm={3}
                                        md={3}
                                        className={
                                          classes.outboundClickValueContainer
                                        }
                                      >
                                        {hd.value &&
                                        hd.value.averageSessionDuration
                                          ? numeral(
                                              hd.value.averageSessionDuration,
                                            ).format("0.0")
                                          : "n/a"}
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                ))}
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      )}
                    <Grid
                      item
                      md={12}
                      sm={12}
                      xs={12}
                      className={classes.contentHubOverviewTitle}
                    >
                      Teasers and Stories
                    </Grid>
                  </>
                )}
              <BigNumber
                title={kpiVocabulary.screenPageViews.description}
                description={kpiVocabulary.screenPageViews.definition}
                value={campaignKPIs ? campaignKPIs.screenPageViews : null}
              />
              <BigNumber
                title={kpiVocabulary.averageSessionDuration.description}
                description={kpiVocabulary.averageSessionDuration.definition}
                value={
                  campaignKPIs ? campaignKPIs.averageSessionDuration : null
                }
                unit="s"
              />
              <BigNumber
                title={kpiVocabulary.ctaClicks.description}
                description={kpiVocabulary.ctaClicks.definition}
                value={
                  campaignKPIs && campaignKPIs.events
                    ? campaignKPIs.events.ctaClicks
                    : null
                }
              />
              <BigNumber
                title={kpiVocabulary.videoPlays.description}
                description={kpiVocabulary.videoPlays.definition}
                value={
                  campaignKPIs && campaignKPIs.events
                    ? campaignKPIs.events.videoPlays
                    : null
                }
              />
              <BigNumber
                title={kpiVocabulary.outboundClicks.description}
                description={kpiVocabulary.outboundClicks.definition}
                value={
                  campaignKPIs && campaignKPIs.events
                    ? campaignKPIs.events.outboundClicks
                    : null
                }
              />
              <BigNumber
                title={kpiVocabulary.shares.description}
                description={kpiVocabulary.shares.definition}
                value={
                  campaignKPIs && campaignKPIs.events
                    ? campaignKPIs.events.shares
                    : null
                }
              />
              <BigNumber
                title={kpiVocabulary.viewImpressions.description}
                description={kpiVocabulary.viewImpressions.definition}
                value={
                  campaignKPIs && campaignKPIs.DFP
                    ? campaignKPIs.DFP.impressions
                    : null
                }
              />
              <BigNumber
                title={kpiVocabulary.clicks.description}
                description={kpiVocabulary.clicks.definition}
                value={
                  campaignKPIs && campaignKPIs.DFP
                    ? campaignKPIs.DFP.clicks
                    : null
                }
              />
              <BigNumber
                title={kpiVocabulary.ctr.description}
                description={kpiVocabulary.ctr.definition}
                value={
                  campaignKPIs && campaignKPIs.DFP
                    ? (campaignKPIs.DFP.clicks / campaignKPIs.DFP.impressions) *
                      100
                    : null
                }
                unit="%"
                decimals={3}
              />
            </Grid>
          </Grid>
        )}

        {Array.isArray(timeSeriesBarData.data) &&
          timeSeriesBarData.data.length > 0 && (
            <Grid item xs={12} sm={12} md={12}>
              <Paper
                className={classes.paperGraph}
                elevation={0}
                variant="outlined"
              >
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    className={classes.graphContainer}
                  >
                    <Grid container spacing={2}>
                      <Grid
                        item
                        xs={9}
                        sm={12}
                        md={12}
                        className={classes.graphTitleContainer}
                      >
                        <Typography variant="h6" className={classes.graphTitle}>
                          Pageviews over time
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={3}
                        sm={false}
                        md={false}
                        className={classes.graphToggleButtonContainer}
                      >
                        <IconButton
                          onClick={toggleShowPageviewGraph}
                          size="large"
                        >
                          {showPageviewGraph ? (
                            <img
                              src="/img/icons/chevron-down.svg"
                              alt="Hide pageview graph"
                              style={{
                                width: "28px",
                                height: "28px",
                                transform: "rotate(180deg)",
                              }}
                            />
                          ) : (
                            <img
                              src="/img/icons/chevron-down.svg"
                              alt="Show pageview graph"
                              style={{ width: "28px", height: "28px" }}
                            />
                          )}
                        </IconButton>
                      </Grid>
                    </Grid>
                    {showPageviewGraph ? (
                      <BarStackedTS data={timeSeriesBarData} />
                    ) : null}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

        <Grid item xs={12} sm={12} md={12}>
          {stories && stories.length > 0 && (
            <Grid container spacing={2} className={classes.storiesContainer}>
              <Typography
                variant="h6"
                gutterBottom
                style={{ marginTop: "2em" }}
              >
                Teasers and Stories
              </Typography>
              <Grid item xs={12} sm={12} md={12}>
                <Stories
                  className={classes.centeredTabs}
                  classes={classes}
                  stories={stories}
                  kpis={storyKPIs}
                  projectData={projectData}
                  abTests={abTests}
                  gaSummarized={gaSummarized}
                />
              </Grid>
            </Grid>
          )}

          {reportData.longforms.longForms.length > 0 && (
            <Grid item xs={12} sm={12} md={12}>
              <Typography
                variant="h6"
                gutterBottom
                style={{ marginTop: "2em", marginBottom: "1em" }}
              >
                Longforms
              </Typography>
              <Grid container spacing={2} className={classes.storiesContainer}>
                {reportData.longforms.longForms.map((l) => (
                  <div
                    key={l.url}
                    style={{
                      width: "100%",
                      marginTop: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <Longform longform={l} classes={classes} />
                  </div>
                ))}
              </Grid>
            </Grid>
          )}
          {reportData.socialMediaProducts.length > 0 && (
            <Grid container spacing={2} className={classes.storiesContainer}>
              <Grid item xs={12} sm={12} md={12}>
                <SocialProductsStories
                  classes={classes}
                  stories={reportData.socialMediaProducts}
                />
              </Grid>
            </Grid>
          )}
          {reportData.socialMediaStories.length > 0 && (
            <Grid container spacing={2} className={classes.storiesContainer}>
              <Grid item xs={12} sm={12} md={12}>
                <SocialMediaStories
                  className={classes.centeredTabs}
                  classes={classes}
                  stories={reportData.socialMediaStories}
                  abTests={reportData.abTests}
                />
              </Grid>
            </Grid>
          )}
        </Grid>

        {isAdmin && (
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            style={{ paddingLeft: "8px", paddingRight: "8px" }}
          >
            <Accordion>
              <AccordionSummary>
                <Typography variant="h6">Data Raw</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DataExplorer data={reportData} />
              </AccordionDetails>
            </Accordion>
            {/* Data Raw holds the untransformed API payload, so the computed KPIs
                (e.g. storyKPIs.scrollDepthBreakdown) are only visible here. */}
            <Accordion>
              <AccordionSummary>
                <Typography variant="h6">Data Transformed</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DataExplorer data={storySelection} />
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </>
    </Grid>
  );
};

export default Report;
